/**
 * 响应形状的"宽容读取"工具。
 *
 * 为什么需要它：本批页面对的 `nodes` / `checkins` / `points` 等后端模块正在并行实现，
 * 响应体在"数组直出"与 `{ ok:true, data:{ nodes:[...] } }` 之间可能有出入。
 * 契约（方案 4.6）规定了 `{ok,data}` 外层，但 `data` 里面的键名没有逐字写死。
 *
 * 这里的函数只做一件事：**在几种合理形状里找出那个数组**，
 * 找不到就返回空数组。这样"字段名猜错"的后果是"这一块显示空状态"，
 * 而不是整个页面白屏抛未捕获异常（容错要求见任务说明）。
 *
 * 注意：这不是把错误藏起来 —— 真正的失败（网络、403、500）依然由 client 抛出，
 * 由页面显示错误态；这里只兜住"形状不符"。
 */
import { CHECKIN_STATUSES, type CheckinStatus } from '@fht/shared';
import { readFailureDetails } from './failure';

/** 从若干候选键里取出第一个数组；都没有就返回空数组。 */
export function pickArray<T>(source: unknown, ...keys: string[]): T[] {
  if (Array.isArray(source)) return source as T[];
  if (source === null || typeof source !== 'object') return [];

  const record = source as Record<string, unknown>;
  for (const key of keys) {
    const value = record[key];
    if (Array.isArray(value)) return value as T[];
  }
  // 再退一步：单键包装（如 { data: [...] }）
  const data = record.data;
  if (Array.isArray(data)) return data as T[];
  if (data !== null && typeof data === 'object') {
    const nested = data as Record<string, unknown>;
    for (const key of keys) {
      const value = nested[key];
      if (Array.isArray(value)) return value as T[];
    }
  }

  // ------------------------------------------------------------------
  // 开发期告警：把"静默返回空"变成"能看见的问题"。
  //
  // 这类不匹配的可怕之处在于**完全不报错**：页面正常渲染，只是那一块永远是空的。
  // 本项目已经因此踩过多次（共享收件箱用 `invitations`、分页列表统一用 `entries`，
  // 而调用方按领域名猜 `checkin s`/`redemptions`/`comments`），
  // 表现都是"功能看起来在，但数据永远不出现"。
  //
  // 这里在 DEV 下把"对象里确实有数组，但候选键一个都没命中"打出来 ——
  // 生产环境保持安静（避免控制台噪音）。
  // ------------------------------------------------------------------
  if (import.meta.env.DEV) {
    const arrayKeys = Object.keys(record).filter((key) => Array.isArray(record[key]));
    const nestedArrayKeys =
      data !== null && typeof data === 'object' && !Array.isArray(data)
        ? Object.keys(data as Record<string, unknown>).filter((key) =>
            Array.isArray((data as Record<string, unknown>)[key]),
          )
        : [];
    const available = [...new Set([...arrayKeys, ...nestedArrayKeys])];
    if (available.length > 0) {
      console.warn(
        `[shape] pickArray 未命中候选键 ${JSON.stringify(keys)}；` +
          `响应里实际存在的数组键是 ${JSON.stringify(available)}。` +
          '这通常意味着前后端的字段名不一致 —— 请把实际键名加进候选列表。',
      );
    }
  }

  return [];
}

/** 取一个对象；不是对象就返回 fallback。 */
export function pickObject<T>(source: unknown, fallback: T): T {
  if (source === null || typeof source !== 'object' || Array.isArray(source)) return fallback;
  const record = source as Record<string, unknown>;
  // 常见的 { data: {...} } 包装
  if (record.data !== null && typeof record.data === 'object' && !Array.isArray(record.data)) {
    // 只有当外层除 ok/data 外没有别的业务字段时才下钻，避免把 data 字段本身当成包装
    const businessKeys = Object.keys(record).filter((key) => key !== 'ok' && key !== 'data');
    if (businessKeys.length === 0) return record.data as T;
  }
  return source as T;
}

/** 数字字段的安全读取（后端偶尔用字符串传数字）。 */
export function toNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
}

/** 可选数字：缺失 / null / 非法一律返回 null（与"0"区分开）。 */
export function toOptionalNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = toNumber(value, Number.NaN);
  return Number.isFinite(parsed) ? parsed : null;
}

/** 数组字段的安全读取：非数组返回空数组（后端的 `weekdays` 可能是 null）。 */
export function toNumberArray(value: unknown): number[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => toNumber(item, Number.NaN)).filter((n) => Number.isFinite(n));
}

/**
 * 打卡状态的安全读取：**不在白名单里的一律当 `pending`**。
 *
 * 为什么不用 `String(value) as CheckinStatus` 之类的断言：状态是页面分支的依据
 * （`done` 显示 ✅、`submitted` 提示"等家长验收"……），一个拼错的状态字符串
 * 会让那条打卡**看起来什么都没发生**，而这种错误在类型断言下完全不会报错。
 * 白名单用 shared 的 `CHECKIN_STATUSES`，与后端枚举同源。
 */
export function checkinStatusOf(value: unknown): CheckinStatus {
  return typeof value === 'string' && (CHECKIN_STATUSES as readonly string[]).includes(value)
    ? (value as CheckinStatus)
    : 'pending';
}

/**
 * 从 ApiError.details 里尽力解析"越界子项清单"（见方案 1.3 ⑫）。
 * 返回命中的清单与它来自哪个键 —— 键名不同时，页面仍能正确展示中文说明。
 */
const OUT_OF_RANGE_KEYS = ['children', 'outOfRange', 'outOfRangeChildren', 'items', 'conflicts'] as const;

export interface ParsedOutOfRange {
  items: Array<{ id: string; name: string; detail: string }>;
  /** 命中的 details 键；没命中为 null。 */
  key: string | null;
}

export function parseOutOfRangeChildren(details: unknown): ParsedOutOfRange {
  if (details === null || typeof details !== 'object') return { items: [], key: null };
  const record = details as Record<string, unknown>;

  for (const key of OUT_OF_RANGE_KEYS) {
    const value = record[key];
    if (!Array.isArray(value) || value.length === 0) continue;
    const items = value.map((raw, index) => {
      if (raw === null || typeof raw !== 'object') {
        return { id: `item-${index}`, name: String(raw), detail: '' };
      }
      const child = raw as Record<string, unknown>;
      const name = typeof child.name === 'string' && child.name ? child.name : `子项 ${index + 1}`;
      const start = typeof child.startDate === 'string' ? child.startDate : null;
      const end = typeof child.endDate === 'string' ? child.endDate : null;
      const longTerm = child.isLongTerm === true;
      const range = longTerm ? '长期' : start && end ? `${start} ~ ${end}` : (start ?? end ?? '');
      return {
        id: typeof child.id === 'string' ? child.id : `item-${index}`,
        name,
        detail: range,
      };
    });
    return { items, key };
  }
  return { items: [], key: null };
}

/**
 * 把 details 渲染成一行可读文本，便于放进错误提示里。
 *
 * ⚠️ 以前对对象是**整块序列化**（`JSON.stringify` 一整个 details）—— 于是保存失败的
 *    提示里会出现一整块 `{"children":[…]}`（2026-10-18 排查管理员登录页时发现的同款问题）。
 *    现在走 `readFailureDetails()` 的同一套归一化：只挑「哪一处 / 具体原因」，
 *    **绝不输出 JSON**（项目红线：任何失败都不能把原始 JSON 端给用户）。
 */
export function describeDetails(details: unknown): string {
  return readFailureDetails(details)
    .map((detail) => (detail.raw !== '' ? `${detail.raw}：${detail.message}` : detail.message))
    .filter((line) => line.trim() !== '')
    .join('；');
}
